<script lang="typescript">
    import { state } from "./stores.js";
    import { toSlugifiedKeys } from "./utils.js";
    import { getBaseLensImage, getBaseLensName, getCurrency } from "./utils";
    import { fly } from "svelte/transition";
    import { onDestroy } from "svelte";
    import { push } from "svelte-spa-router";
    import { contactLensOptions } from "./contactLensData.js";
    import ContactLensCard from "./ContactLensCard.svelte";
    import LensBenefitCard from "./LensBenefitCard.svelte";
    import ContactLensInfoBlock from "./ContactLensInfoBlock.svelte";
    import { PaymentType } from "./types.js";

    export let params = {};

    const lensCardHeightClass = "h-54";
    const lowerKeyContactLensOptions = toSlugifiedKeys(contactLensOptions);

    const selectedLens = lowerKeyContactLensOptions[params.lens];

    onDestroy(() => window.scrollTo(0, 0));
</script>

<section>
    <div class="items-start py-8 page-container local-container">
        <div class="flex flex-col items-start lg:flex-row">
            <div class="flex-col items-center hidden w-full px-8 lg:flex md:w-1/5 gap-y-4">
                <img
                    class="cursor-pointer"
                    src="/img/contact-lens/subscription-person.png"
                    on:click={() => push("/contact-lenses/subscription/")}
                />
            </div>

            <div
                class="flex flex-col items-center justify-center w-full h-auto px-6 py-8 text-white rounded-md lg:w-3/5 bg-primary"
            >
                <h2 class="text-3xl text-center uppercase">Try contact lenses for free</h2>

                <ContactLensCard
                    name={getBaseLensName(selectedLens, $state.region)}
                    image={getBaseLensImage(selectedLens, $state.region)}
                    baseHeightClass={lensCardHeightClass}
                />
                {#if typeof selectedLens.name === "string"}
                    <div class="w-full my-4">
                        {selectedLens.description}
                    </div>
                {/if}

                <div class="flex flex-row">
                    <div class="flex flex-col items-stretch w-full ">
                        <ContactLensInfoBlock
                            title="Vision type"
                            image="/img/contact-lens/info-vision-type.png"
                            description={selectedLens.visionTypes}
                        />
                        <ContactLensInfoBlock
                            title="Material"
                            image="/img/contact-lens/info-material.png"
                            description={selectedLens.material}
                        />
                        <ContactLensInfoBlock
                            title="UV"
                            image="/img/contact-lens/info-uv.svg"
                            description={selectedLens.uv}
                        />
                    </div>
                    <div class="flex flex-col items-stretch w-full mb-6 ">
                        {#each selectedLens.benefits as benefit}
                            <LensBenefitCard {...benefit} />
                        {/each}
                    </div>
                </div>
                <div
                    class="flex-col w-full my-8 overflow-hidden text-black bg-white rounded-md shadow-sm"
                >
                    <table class="pricing w-full min-w-full px-6 text-lg table-auto table-spacing">
                        <thead class="bg-gray-50">
                            <tr>
                                <th
                                    colspan="2"
                                    class="py-3.5 pl-4 pr-3 text-left font-semibold text-gray-900 sm:pl-6"
                                    >Monthly prices</th
                                >
                            </tr>
                        </thead>
                        <tbody class="bg-white">
                            {#each selectedLens.prices as row}
                                <tr class="">
                                    <td
                                        class="py-4 pl-4 pr-3 font-medium text-gray-900 whitespace-nowrap sm:pl-6"
                                        >{row.description}</td
                                    >
                                    <td
                                        class="relative py-4 pl-3 pr-4 font-medium text-right whitespace-nowrap sm:pr-6"
                                    >
                                        <span class="inline">
                                            {getCurrency($state)}
                                        </span>
                                        {row.price[$state.region].toFixed(2)}
                                    </td>
                                </tr>
                            {/each}
                            <tr class="bg-gray-50">
                                <td
                                    class="py-4 pl-4 pr-3 font-medium text-gray-900 whitespace-nowrap sm:pl-6"
                                    >Per box</td
                                >
                                <td
                                    class="relative py-4 pl-3 pr-4 font-medium text-right whitespace-nowrap sm:pr-6"
                                >
                                    <span class="inline">
                                        {getCurrency($state)}
                                    </span>
                                    {selectedLens.boxPrice[$state.region].toFixed(2)}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div
                    class="flex-col w-full overflow-hidden text-black bg-white rounded-md shadow-sm"
                >
                    <table class="w-full px-6 text-lg table-auto table-spacing codes">
                        <thead />
                        <tbody>
                            {#each Object.entries(selectedLens.codes) as [key, list]}
                                {#if list.length > 0}
                                    <tr>
                                        <td>{PaymentType[key]} codes</td>
                                        {#each list as code}
                                            <td>{code}</td>
                                        {/each}
                                    </tr>
                                {/if}
                            {/each}
                        </tbody>
                    </table>
                </div>
            </div>
            <div class="flex flex-col items-center w-full px-8 lg:w-1/5 gap-y-4">
                <img
                    class="cursor-pointer lg:hidden"
                    src="/img/contact-lens/subscription-person.png"
                    on:click={() => push("/contact-lenses/subscription/")}
                />

                <img
                    class="cursor-pointer"
                    src="/img/contact-lens/subscription-banner.png"
                    on:click={() => push("/contact-lenses/subscription/")}
                />
            </div>
        </div>
    </div>
    <div class="flex items-center justify-center w-full py-4 my-0">
        <button
            class="px-4 mx-auto btn-secondary btn-sm lg:px-8"
            type="button"
            on:click={() => push("/")}
        >
            Start again
        </button>
    </div>
</section>

<style>
    #lens-column:nth-child(2n) {
        @apply bg-primary-fade;
    }

    .table-spacing th {
        @apply py-3.5 pl-4 pr-3 text-left font-semibold text-gray-900 sm:pl-6;
    }
    .table-spacing tr:nth-child(even) {
        @apply bg-gray-50;
    }
    .table-spacing tbody {
        @apply bg-white;
    }
    .table-spacing thead {
        @apply bg-gray-50;
    }
    table.pricing td:first-of-type {
        @apply py-4 pl-4 pr-3 font-medium text-gray-900 whitespace-nowrap sm:pl-6;
    }
    table.pricing td:last-of-type {
        @apply relative py-4 pl-3 pr-4 font-bold text-right whitespace-nowrap sm:pr-6;
    }

    table.codes td:first-of-type {
        @apply py-4 pl-4 pr-3 font-bold text-gray-900 whitespace-nowrap sm:pl-6;
    }
    table.codes td:last-of-type {
        @apply relative py-4 pl-3 pr-4 font-medium text-right whitespace-nowrap sm:pr-6;
    }

    .local-container {
        @apply flex-row justify-center flex-wrap sm:flex-nowrap;
        @apply overflow-y-hidden;
    }
</style>
